import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  ArrowLeft, 
  Edit, 
  Play, 
  User, 
  Calendar, 
  Target, 
  AlertTriangle,
  CheckCircle,
  FileText,
  History,
  ClipboardList,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Wand2
} from 'lucide-react';
import { Button, Card, CardHeader, CardContent, LoadingSpinner, ErrorMessage, StatusBadge } from '@/core/components';
import { usePermissions } from '@/core/hooks';
import { formatDate } from '@/core/utils';
import { useCarePlan, useActivateCarePlan } from '../hooks';
import { COMMON_TASK_TEMPLATES } from '../constants/task-templates';
import type { CarePlan, TaskTemplate } from '../types';

interface LocalConcern {
  id: string;
  clientName: string;
  category: string;
  severity: 'low' | 'medium' | 'high';
  details: string;
  actionTaken: string;
  recordedAt: string;
}

interface ChangeHistoryItem {
  id: string;
  carePlanId: string;
  title: string;
  detail: string;
  createdAt: string;
}

const CARE_PLAN_HISTORY_KEY = 'admin:care-plan-change-history';

function readLocalConcerns(): LocalConcern[] {
  try {
    const raw = localStorage.getItem('caregiver:concerns');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.value) ? parsed.value : [];
  } catch {
    return [];
  }
}

function readHistory(): ChangeHistoryItem[] {
  try {
    const raw = localStorage.getItem(CARE_PLAN_HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.value) ? parsed.value : [];
  } catch {
    return [];
  }
}

function writeHistory(items: ChangeHistoryItem[]) {
  localStorage.setItem(CARE_PLAN_HISTORY_KEY, JSON.stringify({ value: items, timestamp: Date.now() }));
}

function getMissingCarePlanFields(carePlan: CarePlan): string[] {
  return [
    !carePlan.assessmentSummary && 'Assessment summary',
    carePlan.goals.length === 0 && 'Goals',
    carePlan.interventions.length === 0 && 'Interventions',
    carePlan.taskTemplates.length === 0 && 'Task templates',
    !carePlan.reviewDate && 'Review date',
    !carePlan.expirationDate && 'Expiration date',
    !carePlan.estimatedHoursPerWeek && 'Estimated hours/week',
  ].filter(Boolean) as string[];
}

function getSuggestedTasks(carePlan: CarePlan): Omit<TaskTemplate, 'id'>[] {
  const text = [
    carePlan.name,
    carePlan.planType,
    carePlan.assessmentSummary,
    carePlan.notes,
    ...(carePlan.functionalLimitations ?? []),
    ...carePlan.goals.map((goal) => `${goal.name} ${goal.description} ${goal.category}`),
  ].join(' ').toLowerCase();

  const names = new Set<string>();
  if (text.includes('medication')) names.add('Morning Medications');
  if (text.includes('mobility') || text.includes('fall') || text.includes('transfer')) {
    names.add('Transfer Assistance');
    names.add('Safety Check');
  }
  if (text.includes('meal') || text.includes('nutrition') || text.includes('hydration')) names.add('Meal Preparation');
  if (text.includes('compan')) names.add('Companionship Activity');
  if (text.includes('personal') || text.includes('adl')) names.add('Morning Personal Care');
  names.add('Visit Documentation');

  return COMMON_TASK_TEMPLATES.filter((template) => names.has(template.name)).slice(0, 6);
}

function getRiskSuggestions(carePlan: CarePlan, concerns: LocalConcern[]) {
  return [
    carePlan.priority === 'HIGH' || carePlan.priority === 'URGENT'
      ? 'High-priority care plan: confirm escalation contacts and review frequency.'
      : '',
    !carePlan.taskTemplates.some((task) => task.category === 'MONITORING')
      ? 'Add safety or wellbeing monitoring task evidence.'
      : '',
    concerns.some((concern) => concern.severity === 'high')
      ? 'Recent high-severity concern: review risk assessment and safeguarding actions.'
      : '',
    carePlan.goals.some((goal) => goal.status === 'AT_RISK')
      ? 'At-risk goal detected: update intervention and task coverage.'
      : '',
  ].filter(Boolean);
}

function getNeedTaskGaps(carePlan: CarePlan, suggestedTasks: Omit<TaskTemplate, 'id'>[]) {
  const existingNames = new Set(carePlan.taskTemplates.map((task) => task.name.toLowerCase()));
  return suggestedTasks.filter((task) => !existingNames.has(task.name.toLowerCase()));
}

export const CarePlanDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermissions();
  const { data: carePlan, isLoading, error, refetch } = useCarePlan(id);
  const activateCarePlan = useActivateCarePlan();
  const [now] = useState(() => Date.now());
  const [aiReview, setAiReview] = useState('');
  const [isGeneratingReview, setIsGeneratingReview] = useState(false);
  const [history, setHistory] = useState<ChangeHistoryItem[]>(() => readHistory());

  const handleActivate = async () => {
    if (!carePlan) return;
    
    try {
      await activateCarePlan.mutateAsync(carePlan.id);
      refetch();
    } catch {
      // Error is handled by the mutation
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !carePlan) {
    return (
      <ErrorMessage
        message={(error as Error)?.message || 'Failed to load care plan'}
        retry={refetch}
      />
    );
  }
  
  const isExpiringSoon = carePlan.expirationDate && 
    new Date(carePlan.expirationDate) <= new Date(now + 30 * 24 * 60 * 60 * 1000);

  const isOverdue = carePlan.reviewDate && 
    new Date(carePlan.reviewDate) < new Date(now);

  const achievedGoalsCount = carePlan.goals.filter(g => g.status === 'ACHIEVED').length;
  const inProgressGoalsCount = carePlan.goals.filter(g => 
    ['IN_PROGRESS', 'ON_TRACK'].includes(g.status)
  ).length;
  const localConcerns = readLocalConcerns().filter((concern) =>
    concern.clientName.toLowerCase().includes(carePlan.clientId.toLowerCase()) ||
    carePlan.name.toLowerCase().includes(concern.clientName.toLowerCase())
  );
  const missingFields = getMissingCarePlanFields(carePlan);
  const suggestedTasks = getSuggestedTasks(carePlan);
  const riskSuggestions = getRiskSuggestions(carePlan, localConcerns);
  const taskGaps = getNeedTaskGaps(carePlan, suggestedTasks);
  const planHistory = history.filter((item) => item.carePlanId === carePlan.id);
  const reviewDueInDays = carePlan.reviewDate
    ? Math.ceil((new Date(carePlan.reviewDate).getTime() - now) / (24 * 60 * 60 * 1000))
    : null;

  const recordHistory = (title: string, detail: string) => {
    const item: ChangeHistoryItem = {
      id: `care-plan-history-${Date.now()}`,
      carePlanId: carePlan.id,
      title,
      detail,
      createdAt: new Date().toISOString(),
    };
    const nextHistory = [item, ...history].slice(0, 100);
    setHistory(nextHistory);
    writeHistory(nextHistory);
    toast.success('Care plan history updated');
  };

  const handleGenerateCarePlanReview = async () => {
    setIsGeneratingReview(true);
    try {
      const response = await fetch('/api/ai/admin-assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'ADMIN',
          page: 'care-plan-detail',
          question: 'Review this UK home care care plan. Provide missing fields, suggested task updates, risk assessment prompts, review/expiry alerts, and recommendations after concerns or incidents. Use only the context and mark output as draft for human approval.',
          context: {
            carePlan,
            missingFields,
            suggestedTasks,
            taskGaps,
            riskSuggestions,
            localConcerns,
            reviewDueInDays,
            isExpiringSoon,
            isOverdue,
          },
        }),
      });

      if (!response.ok) throw new Error(`AI returned ${response.status}`);
      const payload = (await response.json()) as { text?: string };
      const text = payload.text?.trim() || 'No AI recommendation returned.';
      setAiReview(text);
      recordHistory('AI care plan review generated', text.slice(0, 240));
    } catch {
      const fallback = [
        missingFields.length ? `Missing fields: ${missingFields.join(', ')}.` : 'No required field gaps detected.',
        taskGaps.length ? `Suggested task additions: ${taskGaps.map((task) => task.name).join(', ')}.` : 'Task templates appear aligned with detected needs.',
        riskSuggestions.length ? `Risk prompts: ${riskSuggestions.join(' ')}` : 'No urgent risk prompt detected from available data.',
      ].join('\n');
      setAiReview(fallback);
      toast.error('AI review unavailable. Standard checks were generated.');
    } finally {
      setIsGeneratingReview(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/care-plans">
          <Button variant="ghost" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" />}>
            Back
          </Button>
        </Link>
      </div>

      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{carePlan.name}</h1>
          <p className="text-gray-600 mt-1">{carePlan.planNumber}</p>
          <p className="text-sm text-gray-500 mt-1">
            {carePlan.planType.replace(/_/g, ' ')}
          </p>
        </div>
        <div className="flex gap-2">
          <StatusBadge status={carePlan.status} />
          {can('care_plans:write') && (
            <>
              {carePlan.status === 'DRAFT' && (
                <Button
                  leftIcon={<Play className="h-4 w-4" />}
                  onClick={handleActivate}
                  isLoading={activateCarePlan.isPending}
                >
                  Activate
                </Button>
              )}
              <Link to={`/care-plans/${carePlan.id}/edit`}>
                <Button variant="outline" leftIcon={<Edit className="h-4 w-4" />}>
                  Edit
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Alerts */}
      {(isExpiringSoon || isOverdue) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isExpiringSoon && (
            <div className="flex items-center gap-3 p-4 bg-orange-50 border border-orange-200 rounded-md">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="text-sm font-medium text-orange-800">Expiring Soon</p>
                <p className="text-sm text-orange-600">
                  This care plan expires on {formatDate(carePlan.expirationDate!)}
                </p>
              </div>
            </div>
          )}
          {isOverdue && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-md">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <div>
                <p className="text-sm font-medium text-red-800">Review Overdue</p>
                <p className="text-sm text-red-600">
                  Review was due on {formatDate(carePlan.reviewDate!)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <Card>
        <CardHeader title="AI care plan intelligence" />
        <CardContent>
          <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-4">
                <div className={`rounded-lg border p-4 ${missingFields.length ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Missing fields</p>
                  <p className="mt-2 text-2xl font-bold text-slate-950">{missingFields.length}</p>
                </div>
                <div className={`rounded-lg border p-4 ${taskGaps.length ? 'border-blue-200 bg-blue-50' : 'border-emerald-200 bg-emerald-50'}`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Task gaps</p>
                  <p className="mt-2 text-2xl font-bold text-slate-950">{taskGaps.length}</p>
                </div>
                <div className={`rounded-lg border p-4 ${reviewDueInDays !== null && reviewDueInDays <= 14 ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Review</p>
                  <p className="mt-2 text-2xl font-bold text-slate-950">
                    {reviewDueInDays === null ? 'Set' : reviewDueInDays < 0 ? 'Late' : `${reviewDueInDays}d`}
                  </p>
                </div>
                <div className={`rounded-lg border p-4 ${riskSuggestions.length ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Risk prompts</p>
                  <p className="mt-2 text-2xl font-bold text-slate-950">{riskSuggestions.length}</p>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="flex items-center gap-2 font-semibold text-slate-950">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    Missing field detector
                  </p>
                  <div className="mt-3 space-y-2">
                    {missingFields.length === 0 ? (
                      <p className="text-sm text-emerald-700">No important gaps detected.</p>
                    ) : (
                      missingFields.map((field) => (
                        <div key={field} className="rounded-md bg-white px-3 py-2 text-sm text-slate-700">{field}</div>
                      ))
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <p className="flex items-center gap-2 font-semibold text-blue-950">
                    <ClipboardList className="h-4 w-4 text-blue-700" />
                    Needs vs allocated tasks
                  </p>
                  <div className="mt-3 space-y-2">
                    {taskGaps.length === 0 ? (
                      <p className="text-sm text-blue-900">Task templates match detected needs.</p>
                    ) : (
                      taskGaps.map((task) => (
                        <div key={task.name} className="rounded-md bg-white px-3 py-2 text-sm text-blue-900">
                          Add: {task.name}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="flex items-center gap-2 font-semibold text-amber-950">
                  <ShieldCheck className="h-4 w-4 text-amber-700" />
                  Risk assessment and incident/concern recommendations
                </p>
                <div className="mt-3 grid gap-2 md:grid-cols-2">
                  {(riskSuggestions.length > 0 ? riskSuggestions : ['No urgent risk recommendation detected from available data.']).map((risk) => (
                    <div key={risk} className="rounded-md bg-white px-3 py-2 text-sm text-amber-900">{risk}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-lg border border-[#6c3a80]/20 bg-[#6c3a80]/5 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-semibold text-slate-950">AI review and update recommendations</p>
                    <p className="mt-1 text-sm text-slate-600">Draft review notes for human approval.</p>
                  </div>
                  <Button onClick={() => void handleGenerateCarePlanReview()} disabled={isGeneratingReview}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {isGeneratingReview ? 'Reviewing...' : 'Generate review'}
                  </Button>
                </div>
                {aiReview ? (
                  <p className="mt-4 whitespace-pre-line text-sm leading-6 text-slate-800">{aiReview}</p>
                ) : (
                  <p className="mt-4 text-sm leading-6 text-slate-600">
                    Use this to generate draft updates after incidents, concerns, expired reviews or changed needs.
                  </p>
                )}
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="flex items-center gap-2 font-semibold text-slate-950">
                  <History className="h-4 w-4 text-slate-600" />
                  Change history
                </p>
                <div className="mt-3 space-y-2">
                  {planHistory.length === 0 ? (
                    <p className="text-sm text-slate-500">No local change history yet.</p>
                  ) : (
                    planHistory.slice(0, 5).map((item) => (
                      <div key={item.id} className="rounded-md bg-slate-50 p-3 text-sm">
                        <p className="font-semibold text-slate-900">{item.title}</p>
                        <p className="mt-1 text-slate-600">{item.detail}</p>
                        <p className="mt-1 text-xs text-slate-500">{new Date(item.createdAt).toLocaleString('en-GB')}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <p className="flex items-center gap-2 font-semibold text-emerald-950">
                  <RefreshCw className="h-4 w-4 text-emerald-700" />
                  Periodic review automation
                </p>
                <p className="mt-2 text-sm leading-6 text-emerald-900">
                  Review status: {isOverdue ? 'overdue' : reviewDueInDays === null ? 'date missing' : reviewDueInDays <= 14 ? 'due soon' : 'scheduled'}.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => recordHistory('Periodic review scheduled', `Review action recorded for ${carePlan.name}`)}
                >
                  Record review action
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Plan Information */}
          <Card>
            <CardHeader title="Plan Information" />
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Client ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">{carePlan.clientId}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Organization ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">{carePlan.organizationId}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Effective Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(carePlan.effectiveDate)}
                  </dd>
                </div>
                {carePlan.expirationDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Expiration Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(carePlan.expirationDate)}
                    </dd>
                  </div>
                )}
                {carePlan.reviewDate && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Review Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(carePlan.reviewDate)}
                    </dd>
                  </div>
                )}
                {carePlan.estimatedHoursPerWeek && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Estimated Hours/Week</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {carePlan.estimatedHoursPerWeek}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Priority</dt>
                  <dd className="mt-1">
                    <span className={`text-sm font-medium ${
                      carePlan.priority === 'URGENT' ? 'text-red-600' :
                      carePlan.priority === 'HIGH' ? 'text-orange-600' :
                      carePlan.priority === 'MEDIUM' ? 'text-yellow-600' :
                      'text-gray-600'
                    }`}>
                      {carePlan.priority}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Compliance Status</dt>
                  <dd className="mt-1">
                    <StatusBadge status={carePlan.complianceStatus} />
                  </dd>
                </div>
              </dl>

              {carePlan.assessmentSummary && (
                <div className="mt-4">
                  <dt className="text-sm font-medium text-gray-500">Assessment Summary</dt>
                  <dd className="mt-1 text-sm text-gray-900">{carePlan.assessmentSummary}</dd>
                </div>
              )}

              {carePlan.notes && (
                <div className="mt-4">
                  <dt className="text-sm font-medium text-gray-500">Notes</dt>
                  <dd className="mt-1 text-sm text-gray-900">{carePlan.notes}</dd>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Goals */}
          <Card>
            <CardHeader title={`Goals (${carePlan.goals.length})`} />
            <CardContent>
              <div className="space-y-4">
                {carePlan.goals.map((goal) => (
                  <div key={goal.id} className="border-b border-gray-200 last:border-0 pb-4 last:pb-0">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{goal.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{goal.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {goal.category.replace(/_/g, ' ')} • Priority: {goal.priority}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <StatusBadge status={goal.status} />
                        {goal.progressPercentage !== undefined && (
                          <div className="text-sm text-gray-600">
                            {goal.progressPercentage}% Complete
                          </div>
                        )}
                      </div>
                    </div>
                    {goal.targetDate && (
                      <div className="mt-2 text-sm text-gray-600">
                        Target: {formatDate(goal.targetDate)}
                      </div>
                    )}
                    {goal.achievedDate && (
                      <div className="mt-2 text-sm text-green-600">
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        Achieved on {formatDate(goal.achievedDate)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Interventions */}
          <Card>
            <CardHeader title={`Interventions (${carePlan.interventions.length})`} />
            <CardContent>
              <div className="space-y-4">
                {carePlan.interventions.map((intervention) => (
                  <div key={intervention.id} className="border-b border-gray-200 last:border-0 pb-4 last:pb-0">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{intervention.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">{intervention.description}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {intervention.category.replace(/_/g, ' ')}
                        </p>
                      </div>
                      <StatusBadge status={intervention.status} />
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      <p>{intervention.instructions}</p>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Started: {formatDate(intervention.startDate)}
                      {intervention.endDate && ` • Ends: ${formatDate(intervention.endDate)}`}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <Card>
            <CardHeader title="Quick Stats" />
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Goals</span>
                  <span className="text-sm font-medium">{carePlan.goals.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Achieved</span>
                  <span className="text-sm font-medium text-green-600">{achievedGoalsCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">In Progress</span>
                  <span className="text-sm font-medium text-blue-600">{inProgressGoalsCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Interventions</span>
                  <span className="text-sm font-medium">{carePlan.interventions.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Task Templates</span>
                  <span className="text-sm font-medium">{carePlan.taskTemplates.length}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader title="Quick Actions" />
            <CardContent>
              <div className="space-y-2">
                <Link to={`/tasks?carePlanId=${carePlan.id}`}>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <Target className="h-4 w-4 mr-2" />
                    View Tasks
                  </Button>
                </Link>
                <Link to={`/care-plans/${carePlan.id}/progress-notes`}>
                  <Button variant="outline" size="sm" className="w-full justify-start">
                    <FileText className="h-4 w-4 mr-2" />
                    Progress Notes
                  </Button>
                </Link>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <User className="h-4 w-4 mr-2" />
                  Assign Caregiver
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <Calendar className="h-4 w-4 mr-2" />
                  Schedule Review
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader title="Timeline" />
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="h-2 w-2 mt-2 rounded-full bg-blue-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Care Plan Created</p>
                    <p className="text-xs text-gray-600">
                      {formatDate(carePlan.createdAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="h-2 w-2 mt-2 rounded-full bg-green-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Effective Date</p>
                    <p className="text-xs text-gray-600">
                      {formatDate(carePlan.effectiveDate)}
                    </p>
                  </div>
                </div>
                {carePlan.lastReviewedDate && (
                  <div className="flex items-start gap-3">
                    <div className="h-2 w-2 mt-2 rounded-full bg-yellow-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Last Reviewed</p>
                      <p className="text-xs text-gray-600">
                        {formatDate(carePlan.lastReviewedDate)}
                      </p>
                    </div>
                  </div>
                )}
                {carePlan.lastComplianceCheck && (
                  <div className="flex items-start gap-3">
                    <div className="h-2 w-2 mt-2 rounded-full bg-purple-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Compliance Check</p>
                      <p className="text-xs text-gray-600">
                        {formatDate(carePlan.lastComplianceCheck)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
